import { Icon } from '@iconify/react'
import type {
  CampaignPremiatedLuckyNumberEntry,
  CampaignPremiatedNumberAssignment,
  CampaignPremiatedNumberAssignmentDonor,
} from '@/lib/campaigns'
import styles from './CampanhaEditPage.module.css'

export type CampanhaPremiatedNumbersTabProps = {
  entries: CampaignPremiatedLuckyNumberEntry[]
  maxDigitsPerNumber: number
  assignments: CampaignPremiatedNumberAssignment[] | undefined
  onEntriesChange: (next: CampaignPremiatedLuckyNumberEntry[]) => void
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

function organizerDonorDisplayName(donor: CampaignPremiatedNumberAssignmentDonor): string {
  if (donor.person_type === 'pj') {
    const corporate = donor.corporate_name?.trim()
    if (corporate) return corporate
  }
  const full = donor.full_name?.trim()
  if (full) return full
  const short = donor.name?.trim()
  if (short) return short
  return 'Participante'
}

function formatDocumentForOrganizer(donor: CampaignPremiatedNumberAssignmentDonor): string {
  if (donor.person_type === 'pj') {
    const cnpj = donor.document_cnpj?.trim()
    return cnpj ?? '—'
  }
  const cpf = donor.document_cpf?.trim()
  return cpf ?? '—'
}

function formatAssignedAtBr(iso: string | null | undefined): string {
  if (!iso?.trim()) return '—'
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(ms))
}

/** Dígitos no formato internacional esperado pelo wa.me (sem símbolos, sem +). */
function phoneDigitsForWhatsApp(phone: string): string | null {
  const d = digitsOnly(phone)
  if (d.length < 10) return null
  if (d.startsWith('55') && d.length >= 12) return d
  if (d.length === 10 || d.length === 11) return `55${d}`
  return d.length >= 12 ? d : null
}

function firstNameFromDisplay(displayName: string): string {
  const t = displayName.trim()
  if (t === '') return 'Participante'
  const parts = t.split(/\s+/)
  return parts[0] ?? t
}

function buildPremiatedWinnerWhatsAppMessage(params: {
  recipientDisplayName: string
  luckyNumber: string
  prizeLine: string
}): string {
  const first = firstNameFromDisplay(params.recipientDisplayName)
  const prize =
    params.prizeLine.trim() !== '' ? params.prizeLine.trim() : 'o prêmio configurado para este número premiado'

  return [
    `Olá, ${first}!`,
    '',
    `Temos uma excelente notícia: o seu número da sorte *${params.luckyNumber}* foi contemplado na nossa campanha.`,
    '',
    `Prêmio correspondente: *${prize}*.`,
    '',
    'Parabéns por essa conquista. Em breve seguimos com os próximos passos para a liberação do benefício, conforme o regulamento.',
    '',
    'Se tiver qualquer dúvida, responda esta mensagem e teremos prazer em ajudar.',
    '',
    'Um abraço,',
    'Equipe da campanha',
  ].join('\n')
}

function PremiatedPhoneTableCell({ row }: { row: CampaignPremiatedNumberAssignment }) {
  const rawPhone = row.donor?.phone?.trim()
  if (!rawPhone || !row.donor) return '—'

  const waDigits = phoneDigitsForWhatsApp(rawPhone)
  if (!waDigits) {
    return <span>{rawPhone}</span>
  }

  const displayName = organizerDonorDisplayName(row.donor)
  const prizeLine = row.prize_description?.trim() ?? ''
  const text = buildPremiatedWinnerWhatsAppMessage({
    recipientDisplayName: displayName,
    luckyNumber: row.number,
    prizeLine,
  })
  const href = `https://wa.me/${waDigits}?text=${encodeURIComponent(text)}`

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.premiatedWhatsAppLink}
      aria-label={`Abrir WhatsApp para falar com ${displayName}`}
    >
      {rawPhone}
      <Icon icon="mdi:whatsapp" aria-hidden width={18} height={18} />
    </a>
  )
}

export function CampanhaPremiatedNumbersTab({
  entries,
  maxDigitsPerNumber,
  assignments,
  onEntriesChange,
}: CampanhaPremiatedNumbersTabProps) {
  const rows = entries.length > 0 ? entries : [{ number: '', prize_description: '' }]

  const updateRow = (index: number, patch: Partial<CampaignPremiatedLuckyNumberEntry>) => {
    const next = rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    onEntriesChange(next)
  }

  const updateNumber = (index: number, value: string) => {
    let cleaned = digitsOnly(value)
    if (cleaned.length > maxDigitsPerNumber) {
      cleaned = cleaned.slice(0, maxDigitsPerNumber)
    }
    updateRow(index, { number: cleaned })
  }

  const addRow = () => {
    onEntriesChange([...rows, { number: '', prize_description: '' }])
  }

  const removeRow = (index: number) => {
    const filtered = rows.filter((_, rowIndex) => rowIndex !== index)
    onEntriesChange(filtered)
  }

  const hasConfiguredNumbers = entries.some((e) => digitsOnly(e.number).length > 0)
  const showAssignmentsPanel = assignments !== undefined && assignments.length > 0

  return (
    <section className={styles.editSection} aria-labelledby="section-premiated-numbers">
      <h2 id="section-premiated-numbers" className={styles.editSectionTitle}>
        <Icon className={styles.editSectionTitleIcon} icon="mdi:trophy-variant" aria-hidden />
        Números premiados
      </h2>
      <div className={styles.editSectionBody}>
        <div className={styles.editSubsection}>
          <p className={styles.editLabelHint} style={{ marginBottom: '1rem' }}>
            Defina quais números da sorte são considerados premiados nesta campanha e a descrição do prêmio de cada um.
            Use apenas dígitos no número, sem espaços ou símbolos. Números duplicados são ignorados ao salvar.
          </p>
          <p className={styles.editLabelHint} style={{ marginBottom: '1.25rem' }}>
            {maxDigitsPerNumber < 32
              ? `Com a quantidade total de números já definida para esta campanha, cada bilhete usa ${maxDigitsPerNumber} dígitos (zeros à esquerda quando necessário).`
              : 'Após definir a quantidade total de números da campanha, cada bilhete passará a seguir o mesmo tamanho em dígitos usado na geração.'}
          </p>
          <div className={styles.editField} style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {rows.map((rowValue, index) => (
                <div
                  key={`premiated-row-${index}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    paddingBottom: '1rem',
                    borderBottom: index < rows.length - 1 ? '1px solid rgba(0, 0, 0, 0.08)' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <label className={styles.editLabel} htmlFor={`premiated-number-${index}`} style={{ minWidth: 0 }}>
                      Número premiado {index + 1}
                    </label>
                    <input
                      id={`premiated-number-${index}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className={styles.editInput}
                      style={{ maxWidth: 280, flex: '1 1 200px' }}
                      maxLength={maxDigitsPerNumber}
                      placeholder="Ex.: 111111"
                      value={rowValue.number}
                      onChange={(e) => updateNumber(index, e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.editBackButton}
                      style={{ padding: '8px 12px', fontSize: '0.85rem' }}
                      onClick={() => removeRow(index)}
                      disabled={rows.length === 1 && digitsOnly(rowValue.number) === '' && rowValue.prize_description.trim() === ''}
                      aria-label={`Remover número premiado ${index + 1}`}
                    >
                      <Icon icon="mdi:delete-outline" aria-hidden />
                      Remover
                    </button>
                  </div>
                  <div className={styles.editField} style={{ marginBottom: 0 }}>
                    <label className={styles.editLabel} htmlFor={`premiated-prize-${index}`}>
                      Prêmio deste número
                      <span className={styles.editLabelHint}>(texto exibido na landing e no painel, por exemplo valor no PIX)</span>
                    </label>
                    <textarea
                      id={`premiated-prize-${index}`}
                      className={styles.editTextarea}
                      rows={3}
                      maxLength={2000}
                      placeholder="Ex.: R$ 1.000,00 no PIX"
                      value={rowValue.prize_description}
                      onChange={(e) => updateRow(index, { prize_description: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button type="button" className={styles.exampleEmailBtn} onClick={addRow}>
                <Icon icon="mdi:plus" aria-hidden />
                Adicionar número
              </button>
            </div>
          </div>
        </div>

        {showAssignmentsPanel && (
          <div className={styles.editSubsection} style={{ marginTop: '1.75rem' }}>
            <h3 className={styles.editSubsectionTitle}>Ganhadores por número premiado</h3>
            <p className={styles.editLabelHint} style={{ marginBottom: '1rem' }}>
              Lista alinhada aos números salvos na campanha. Os dados do participante aparecem após confirmação do
              pagamento e atribuição do bilhete correspondente.
            </p>
            <div className={styles.premiatedAssignmentsScroll}>
              <table className={styles.premiatedAssignmentsTable}>
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">Prêmio (texto)</th>
                    <th scope="col">Participante</th>
                    <th scope="col">E-mail</th>
                    <th scope="col">Telefone</th>
                    <th scope="col">CPF ou CNPJ</th>
                    <th scope="col">Pagamento</th>
                    <th scope="col">Atribuído em</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((row: CampaignPremiatedNumberAssignment) => (
                    <tr key={row.number}>
                      <td>{row.number}</td>
                      <td>{row.prize_description?.trim() ? row.prize_description : '—'}</td>
                      <td>{row.donor ? organizerDonorDisplayName(row.donor) : 'Sem ganhador ainda'}</td>
                      <td>{row.donor?.email?.trim() ? row.donor.email : '—'}</td>
                      <td>
                        <PremiatedPhoneTableCell row={row} />
                      </td>
                      <td>{row.donor ? formatDocumentForOrganizer(row.donor) : '—'}</td>
                      <td>{row.payment_id != null ? String(row.payment_id) : '—'}</td>
                      <td>{formatAssignedAtBr(row.assigned_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {assignments === undefined && hasConfiguredNumbers && (
          <p className={styles.editLabelHint} style={{ marginTop: '1.25rem' }}>
            Salve a campanha para atualizar a lista de ganhadores com base nos pagamentos confirmados.
          </p>
        )}
      </div>
    </section>
  )
}
